// src/components/Client/ClientDetailsDrawer.jsx
import { useEffect, useState, useMemo } from "react";
import {
  FaUser,
  FaEnvelope,
  FaMapMarkerAlt,
  FaPaw,
  FaCalendarAlt,
  FaEdit,
  FaArrowLeft,
  FaRupeeSign,
  FaPhone,
  FaExclamationTriangle,
  FaFileAlt,
  FaExternalLinkAlt,
} from "react-icons/fa";
import { format } from "date-fns";
import { httpService } from "../../services/httpService";
import LoaderSpiner from "../../components/LoaderSpiner/LoaderSpiner";

const ClientDetailsDrawer = ({
  clientId,
  clientData: initialClientData,
  onClose,
  petAppointments = [],
  petBoarding = [],
  onEditClick,
  isOpen,
}) => {
  const [overviewData, setOverviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Determine active client ID from prop or passed client object
  const activeClientId =
    clientId || initialClientData?._id || initialClientData?.client_id;

  // Simple scroll prevention
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Fetch Client Overview Data by ID
  useEffect(() => {
    if (!isOpen || !activeClientId) return;

    const fetchClientOverview = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await httpService.get(
          `/vendorclient/getVendorClientPetsOverviewById/${activeClientId}`,
        );

        if (response.code === 200 || response.status) {
          const rawData = response.data?.data || response.data;
          setOverviewData(rawData);
        } else {
          setError("Failed to fetch client details.");
        }
      } catch (err) {
        console.error("Error fetching client overview:", err);
        setError(
          err?.response?.data?.message || "Error loading client details.",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchClientOverview();
  }, [isOpen, activeClientId]);

  // Normalized Client Information
  const clientInfo = useMemo(() => {
    const apiDetails =
      overviewData?.client_details || overviewData?.client || {};
    const propDetails = initialClientData || {};

    return {
      firstname:
        apiDetails.firstname ||
        apiDetails.client_first_name ||
        propDetails.firstname ||
        propDetails.client_first_name ||
        "",
      lastname:
        apiDetails.lastname ||
        apiDetails.client_last_name ||
        propDetails.lastname ||
        propDetails.client_last_name ||
        "",
      phone:
        apiDetails.phone || apiDetails.client_phone || propDetails.phone || "",
      email:
        apiDetails.email || apiDetails.client_email || propDetails.email || "",
      address: apiDetails.address || propDetails.address || null,
      createdAt: apiDetails.createdAt || propDetails.createdAt || null,
      labels: apiDetails.labels || propDetails.labels || [],
      id_proof: apiDetails.id_proof || propDetails.id_proof || null,
      documents: apiDetails.documents || propDetails.documents || [],
    };
  }, [overviewData, initialClientData]);

  // Format date safely
  const formatDateSafe = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? "N/A" : format(date, "dd MMM yyyy");
    } catch {
      return "N/A";
    }
  };

  const capitalizeFirstLetter = (text) => {
    if (!text) return "";
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  // Combine and normalize orders from API or fallback props
  const allOrders = useMemo(() => {
    if (!overviewData) return [];

    // Only actual orders.
    // Do NOT use upcoming_appointment_details
    // or completed_cancelled_appointments here.
    const orders = Array.isArray(overviewData.order_details)
      ? overviewData.order_details
      : [];

    return orders
      .map((order) => ({
        ...order,

        // UI helper fields
        orderNumber: order.order_number,

        date: order.createdAt || order.completed_date || order.updatedAt,

        orderType: order.order_type || "order",

        totalAmount: Number(
          order.grand_total ??
            order.amount_after_adjustment ??
            order.total_amount ??
            0,
        ),

        paidAmount: Number(order.total_paid_amount ?? 0),

        pendingAmount: Math.max(
          Number(
            order.grand_total ??
              order.amount_after_adjustment ??
              order.total_amount ??
              0,
          ) - Number(order.total_paid_amount ?? 0),
          0,
        ),

        paymentStatus:
          order.payment_status ||
          (Number(order.total_paid_amount ?? 0) >=
          Number(
            order.grand_total ??
              order.amount_after_adjustment ??
              order.total_amount ??
              0,
          )
            ? "paid"
            : "unpaid"),
      }))
      .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  }, [overviewData]);

  // Calculate Order Statistics
  const orderStats = useMemo(() => {
    if (overviewData?.payment_details) {
      const paymentDetails = overviewData.payment_details || {};
      const received = paymentDetails.received || [];
      const pending = paymentDetails.pending || [];

      const totalPaid = received.reduce(
        (sum, p) => sum + (parseFloat(p.payment_amount) || 0),
        0,
      );
      const totalPending = pending.reduce(
        (sum, p) => sum + (parseFloat(p.balance || p.pending_amount) || 0),
        0,
      );

      return {
        totalOrders: allOrders.length,
        totalAmount: totalPaid + totalPending,
        totalPaid,
        totalPending,
      };
    }

    return allOrders.reduce(
      (acc, order) => {
        acc.totalOrders += 1;
        acc.totalAmount += order.totalAmount;
        acc.totalPaid += order.paidAmount;
        acc.totalPending += order.pendingAmount;
        return acc;
      },
      {
        totalOrders: 0,
        totalAmount: 0,
        totalPaid: 0,
        totalPending: 0,
      },
    );
  }, [overviewData, allOrders]);

  const getOrderIcon = (orderType) => {
    switch (orderType?.toLowerCase()) {
      case "appointment":
        return <FaCalendarAlt className="w-4 h-4 text-blue-500" />;
      case "boarding":
      case "booking":
        return <FaPaw className="w-4 h-4 text-green-500" />;
      default:
        return <FaCalendarAlt className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === "completed") return "bg-green-100 text-green-800";
    if (statusLower === "confirmed" || statusLower === "upcoming")
      return "bg-blue-100 text-blue-800";
    if (statusLower === "pending") return "bg-yellow-100 text-yellow-800";
    if (statusLower === "cancelled" || statusLower === "cancel")
      return "bg-red-100 text-red-800";
    return "bg-gray-100 text-gray-800";
  };

  const getPaymentStatusColor = (paymentStatus) => {
    const statusLower = paymentStatus?.toLowerCase();
    if (statusLower === "paid") return "bg-green-100 text-green-800";
    if (statusLower === "partial") return "bg-yellow-100 text-yellow-800";
    if (statusLower === "unpaid" || statusLower === "pending")
      return "bg-red-100 text-red-800";
    return "bg-gray-100 text-gray-800";
  };

  const formatCurrency = (amount) => {
    return `₹${Number(amount || 0).toFixed(0)}`;
  };

  const CurrencyDisplay = ({ amount, className = "" }) => (
    <div className={`flex items-center ${className}`}>
      <FaRupeeSign className="h-3 w-3 mr-0.5" />
      <span>{Number(amount || 0).toFixed(0)}</span>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex justify-end"
      onClick={onClose}
    >
      <div
        className="h-full w-full lg:w-2/5 bg-white shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 p-3 border-b bg-gradient-to-r from-blue-50 to-indigo-50 sticky top-0 z-10 shrink-0">
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full hover:bg-white/70 flex items-center justify-center transition"
          >
            <FaArrowLeft className="w-5 h-5 text-gray-700" />
          </button>

          <div className="flex items-center gap-3 flex-1">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shrink-0">
              <FaUser className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg font-semibold text-gray-900 truncate">
                    {clientInfo.firstname} {clientInfo.lastname}
                  </h2>

                  {clientInfo.phone && (
                    <div className="flex items-center gap-2 mt-0.5 text-sm text-gray-600">
                      <FaPhone className="w-3.5 h-3.5 shrink-0 text-gray-400" />
                      <span>{clientInfo.phone}</span>
                    </div>
                  )}

                  {clientInfo.alternate_phone && (
                    <div className="flex items-center gap-2 mt-0.5 text-sm text-gray-600">
                      <FaPhone className="w-3.5 h-3.5 shrink-0 text-emerald-500" />
                      <span>
                        {clientInfo.alternate_phone}{" "}
                        <span className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-full ml-1">
                          Alt
                        </span>
                      </span>
                    </div>
                  )}

                  {clientInfo.email && (
                    <div className="flex items-center gap-2 mt-0.5 text-sm text-gray-600">
                      <FaEnvelope className="w-3.5 h-3.5 shrink-0 text-gray-400" />
                      <span className="truncate">{clientInfo.email}</span>
                    </div>
                  )}

                  {clientInfo.address &&
                    (clientInfo.address.address1 ||
                      clientInfo.address.city ||
                      typeof clientInfo.address === "string") && (
                      <div className="flex items-start gap-2 mt-0.5 text-sm text-gray-600">
                        <FaMapMarkerAlt className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gray-400" />
                        <span className="line-clamp-2">
                          {typeof clientInfo.address === "string"
                            ? clientInfo.address
                            : [
                                clientInfo.address.address1 ||
                                  clientInfo.address.street,
                                clientInfo.address.city,
                                clientInfo.address.state,
                                clientInfo.address.zip ||
                                  clientInfo.address.zipCode,
                              ]
                                .filter(Boolean)
                                .join(", ")}
                        </span>
                      </div>
                    )}

                  {clientInfo.labels?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {clientInfo.labels.map((label, index) => (
                        <span
                          key={`label-${index}`}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200"
                        >
                          {label}
                        </span>
                      ))}
                    </div>
                  )}

                  {clientInfo.custom_fields &&
                    Object.keys(clientInfo.custom_fields).filter((k) => {
                      const v = clientInfo.custom_fields[k];
                      if (v === undefined || v === null || v === "") return false;
                      if (k.startsWith("sec_") || k.startsWith("sec-")) return false;
                      if (
                        (k.startsWith("fld-") || k.startsWith("fld_")) &&
                        Object.keys(clientInfo.custom_fields).some(
                          (other) =>
                            other !== k &&
                            !other.startsWith("fld-") &&
                            !other.startsWith("fld_") &&
                            String(clientInfo.custom_fields[other]) === String(v),
                        )
                      ) {
                        return false;
                      }
                      return true;
                    }).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {Object.entries(clientInfo.custom_fields)
                          .filter(([k, v]) => {
                            if (v === undefined || v === null || v === "") return false;
                            if (k.startsWith("sec_") || k.startsWith("sec-")) return false;
                            if (
                              (k.startsWith("fld-") || k.startsWith("fld_")) &&
                              Object.keys(clientInfo.custom_fields).some(
                                (other) =>
                                  other !== k &&
                                  !other.startsWith("fld-") &&
                                  !other.startsWith("fld_") &&
                                  String(clientInfo.custom_fields[other]) === String(v),
                              )
                            ) {
                              return false;
                            }
                            return true;
                          })
                          .map(([k, v]) => {
                            const label = k
                              .replace(/^customer\./, "")
                              .replace(/_/g, " ")
                              .replace(/\b\w/g, (c) => c.toUpperCase());
                            return (
                              <span
                                key={k}
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-800 border border-emerald-200"
                              >
                                {label}:{" "}
                                {typeof v === "object"
                                  ? JSON.stringify(v)
                                  : String(v)}
                              </span>
                            );
                          })}
                      </div>
                    )}

                  {(() => {
                    const list = [];
                    const seen = new Set();
                    const p = clientInfo.id_proof;
                    if (p) {
                      const url = typeof p === "string" ? p : p.url;
                      const name =
                        typeof p === "object" && p.name
                          ? p.name
                          : "ID Proof Document";
                      if (url && !seen.has(url)) {
                        seen.add(url);
                        list.push({ name, url });
                      }
                    }
                    const docs = Array.isArray(clientInfo.documents)
                      ? clientInfo.documents
                      : [];
                    for (const d of docs) {
                      if (!d) continue;
                      const url = typeof d === "string" ? d : d.url;
                      if (!url || seen.has(url)) continue;
                      const cat = String(d.category || "").toLowerCase();
                      const name = String(d.name || "").toLowerCase();
                      if (
                        cat === "id_proof" ||
                        cat === "document" ||
                        name.includes("id") ||
                        name.includes("proof") ||
                        name.includes("aadhaar") ||
                        name.includes("pan") ||
                        name.includes("license") ||
                        name.includes("passport")
                      ) {
                        seen.add(url);
                        list.push({
                          name: d.name || `ID Proof ${list.length + 1}`,
                          url,
                        });
                      }
                    }

                    if (!list.length) return null;

                    return (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {list.map((doc, idx) => (
                          <a
                            key={idx}
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md transition shadow-xs"
                            title={`View ${doc.name}`}
                          >
                            <FaFileAlt className="w-3.5 h-3.5 text-blue-600" />
                            <span>
                              {list.length > 1
                                ? doc.name?.length > 25
                                  ? `ID Proof ${idx + 1}`
                                  : doc.name
                                : doc.name}
                            </span>
                            <FaExternalLinkAlt className="w-2.5 h-2.5 text-blue-500 ml-0.5" />
                          </a>
                        ))}
                      </div>
                    );
                  })()}
                </div>

                {onEditClick && (
                  <button
                    onClick={onEditClick}
                    className="w-8 h-8 rounded-full hover:bg-blue-100 flex items-center justify-center transition text-blue-600 hover:text-blue-800 shrink-0"
                    title="Edit Client"
                  >
                    <FaEdit className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Scrollable Content Container */}
        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {loading ? (
            <div className="py-20 flex justify-center items-center">
              <LoaderSpiner />
            </div>
          ) : error ? (
            <div className="py-12 text-center text-red-600 space-y-2">
              <FaExclamationTriangle className="w-10 h-10 mx-auto text-red-400" />
              <p className="font-medium">{error}</p>
            </div>
          ) : (
            <>
              {/* Basic Info Card */}
              {clientInfo.createdAt && (
                <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <FaCalendarAlt className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-500">Client Since:</span>
                    <span className="font-medium text-gray-900">
                      {formatDateSafe(clientInfo.createdAt)}
                    </span>
                  </div>
                </div>
              )}

              {/* Order Summary Stats */}
              <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Overview Statistics
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-2xl font-bold text-blue-600">
                      {orderStats.totalOrders}
                    </p>
                    <p className="text-xs text-gray-600 mt-0.5">Total Orders</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-2xl font-bold text-green-600">
                      {formatCurrency(orderStats.totalAmount)}
                    </p>
                    <p className="text-xs text-gray-600 mt-0.5">Total Amount</p>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <p className="text-2xl font-bold text-purple-600">
                      {formatCurrency(orderStats.totalPaid)}
                    </p>
                    <p className="text-xs text-gray-600 mt-0.5">Total Paid</p>
                  </div>
                  <div className="text-center p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <p className="text-2xl font-bold text-orange-600">
                      {formatCurrency(orderStats.totalPending)}
                    </p>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Pending Payment
                    </p>
                  </div>
                </div>
              </div>

              {/* Recent Orders */}
              <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">
                    Recent Orders
                  </h3>
                  <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
                    {allOrders.length} orders
                  </span>
                </div>

                {allOrders.length > 0 ? (
                  <div className="space-y-3">
                    {allOrders.map((order, index) => (
                      <div
                        key={order._id || index}
                        className="flex items-start gap-3 p-3 border border-gray-100 rounded-lg hover:bg-gray-50 transition"
                      >
                        {/* Order Icon */}
                        {getOrderIcon(order.orderType)}

                        <div className="flex-1 min-w-0">
                          {/* Order Number + Total */}
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-medium text-gray-900 truncate">
                              {order.orderNumber || "Order"}
                            </p>

                            <div className="flex items-center text-sm font-semibold text-gray-700">
                              <CurrencyDisplay amount={order.totalAmount} />
                            </div>
                          </div>

                          {/* Date + Type + Status */}
                          <div className="flex items-center gap-2 text-sm text-gray-600 flex-wrap mb-2">
                            <span>{formatDateSafe(order.date)}</span>

                            <span>•</span>

                            <span className="capitalize">
                              {order.orderType}
                            </span>

                            <span>•</span>

                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                                order.status,
                              )}`}
                            >
                              {capitalizeFirstLetter(order.status || "Unknown")}
                            </span>
                          </div>

                          {/* Payment Information */}
                          <div className="flex items-center justify-between text-xs">
                            <div className="flex items-center gap-4">
                              {/* Paid */}
                              <div className="flex items-center gap-1">
                                <span className="text-gray-500">Paid:</span>

                                <span className="font-semibold text-green-600">
                                  <CurrencyDisplay
                                    amount={order.paidAmount}
                                    className="text-green-600"
                                  />
                                </span>
                              </div>

                              {/* Pending */}
                              <div className="flex items-center gap-1">
                                <span className="text-gray-500">Pending:</span>

                                <span className="font-semibold text-red-600">
                                  <CurrencyDisplay
                                    amount={order.pendingAmount}
                                    className="text-red-600"
                                  />
                                </span>
                              </div>
                            </div>

                            {/* Payment Status */}
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(
                                order.paymentStatus,
                              )}`}
                            >
                              {capitalizeFirstLetter(
                                order.paymentStatus || "Unpaid",
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FaCalendarAlt className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm font-medium">
                      No orders found
                    </p>
                    <p className="text-xs text-gray-400">
                      This client has no recorded order history
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="sticky bottom-0 border-t border-gray-200 bg-white h-3 shrink-0" />
      </div>
    </div>
  );
};

export default ClientDetailsDrawer;
